import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAllSeo, upsertSeo } from '@/lib/api/seo';
import { logActivity as logActivityDb } from '@/lib/api/activity';

const PAGE_LABEL_KEYS: Record<string, string> = {
  home: 'adm2.seo_page_home',
  services: 'adm2.seo_page_services',
  about: 'adm2.seo_page_about',
  pricing: 'adm2.seo_page_pricing',
  gallery: 'adm2.seo_page_gallery',
  team: 'adm2.seo_page_team',
  booking: 'adm2.seo_page_booking',
  contact: 'adm2.seo_page_contact',
};

interface SeoEntry {
  page: string;
  slug: string;
  title: string;
  description: string;
  keywords: string;
  ogImage: string;
}

const DEFAULT_SEO: SeoEntry[] = [
  { page: 'Startseite', slug: 'home', title: 'Esteva Clinic | Premium-Klinik Tirana | Zahnmedizin & Plastische Chirurgie', description: 'Esteva Clinic in Tirana bietet hochwertige Zahnmedizin, plastische Chirurgie und Haartransplantation. Bis zu 70% günstiger als in DE/AT/CH. Jetzt kostenlose Erstberatung buchen.', keywords: 'Esteva Clinic, Zahnmedizin Tirana, Haartransplantation, plastische Chirurgie, Dental-Kosmetik, Veneers, Botox, Premium-Klinik', ogImage: '' },
  { page: 'Leistungen', slug: 'services', title: 'Leistungen | Esteva Clinic | Haartransplantation & Zahnmedizin', description: 'Entdecken Sie alle Behandlungen der Esteva Clinic: Haartransplantation, Dental-Kosmetik, Veneers, Botox und plastische Chirurgie in Tirana, Albanien.', keywords: 'Haartransplantation, Zahnmedizin, Veneers, Botox, Dental-Kosmetik', ogImage: '' },
  { page: 'Über uns', slug: 'about', title: 'Über uns | Esteva Clinic | Premium-Klinik in Tirana', description: 'Erfahren Sie mehr über die Esteva Clinic in Tirana. Unser Team, unsere Philosophie und unsere Mission für hochwertige medizinische Behandlungen.', keywords: 'Über Esteva Clinic, Team Tirana, Premium-Klinik Albanien', ogImage: '' },
  { page: 'Preise', slug: 'pricing', title: 'Preise | Esteva Clinic | Transparente Behandlungskosten', description: 'Alle Preise der Esteva Clinic auf einen Blick. Bis zu 70% günstiger als in Deutschland, Österreich und der Schweiz. Kostenlose Erstberatung.', keywords: 'Preise Zahnmedizin, Haartransplantation Kosten, Veneers Preis, Botox Kosten', ogImage: '' },
  { page: 'Galerie', slug: 'gallery', title: 'Galerie | Esteva Clinic | Vorher-Nachher', description: 'Vorher-Nachher-Galerie der Esteva Clinic. Echte Behandlungsergebnisse aus Zahnmedizin, Haartransplantation und plastischer Chirurgie.', keywords: 'Vorher Nachher, Behandlungsergebnisse, Galerie', ogImage: '' },
  { page: 'Team', slug: 'team', title: 'Team | Esteva Clinic | Unsere Ärzte & Experten', description: 'Lernen Sie das Team der Esteva Clinic kennen. Erfahrene Ärzte und Spezialisten für Zahnmedizin, plastische Chirurgie und Haartransplantation.', keywords: 'Team Esteva Clinic, Ärzte Tirana, Spezialisten', ogImage: '' },
  { page: 'Terminbuchung', slug: 'booking', title: 'Termin buchen | Esteva Clinic | Kostenlose Erstberatung', description: 'Vereinbaren Sie jetzt Ihren Beratungstermin bei der Esteva Clinic in Tirana. Kostenlose Erstberatung für alle Behandlungen.', keywords: 'Termin buchen, Beratungstermin, kostenlose Erstberatung', ogImage: '' },
  { page: 'Kontakt', slug: 'contact', title: 'Kontakt | Esteva Clinic | So erreichen Sie uns', description: 'Kontaktieren Sie die Esteva Clinic in Tirana. Telefon, E-Mail, WhatsApp und Adresse. Wir freuen uns auf Ihre Anfrage.', keywords: 'Kontakt Esteva Clinic, Adresse Tirana, Telefon', ogImage: '' },
];

export default function SeoPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<SeoEntry[]>([]);

  const pageLabel = (e: SeoEntry) => (PAGE_LABEL_KEYS[e.slug] ? t(PAGE_LABEL_KEYS[e.slug]) : e.page);
  const [activePage, setActivePage] = useState('home');
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }
    void (async () => {
      try {
        const rows = await getAllSeo();
        if (rows.length) {
          setEntries(rows.map((r) => ({
            page: r.page ?? r.slug,
            slug: r.slug,
            title: r.title ?? '',
            description: r.description ?? '',
            keywords: r.keywords ?? '',
            ogImage: r.og_image ?? '',
          })));
        } else {
          setEntries(DEFAULT_SEO);
        }
      } catch { setEntries(DEFAULT_SEO); }
    })();
  }, [navigate]);

  const activeEntry = entries.find((e) => e.slug === activePage);

  const handleSave = async () => {
    try {
      await Promise.all(entries.map((e) => upsertSeo({
        slug: e.slug,
        page: e.page,
        title: e.title,
        description: e.description,
        keywords: e.keywords,
        og_image: e.ogImage,
      })));
      await logActivityDb('seo_updated', `SEO für "${activeEntry?.page}" aktualisiert`);
      setSavedMsg(t('adm2.saved'));
    } catch {
      setSavedMsg(t('adm2.save_error'));
    }
    setTimeout(() => setSavedMsg(''), 2000);
  };

  const updateEntry = (key: keyof SeoEntry, value: string) => {
    setEntries((prev) =>
      prev.map((e) => (e.slug === activePage ? { ...e, [key]: value } : e))
    );
  };

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  const getPreview = () => {
    if (!activeEntry) return null;
    return {
      title: activeEntry.title.slice(0, 60) + (activeEntry.title.length > 60 ? '...' : ''),
      description: activeEntry.description.slice(0, 160) + (activeEntry.description.length > 160 ? '...' : ''),
      url: `https://esteva.ch/${activeEntry.slug === 'home' ? '' : activeEntry.slug}`,
    };
  };

  const preview = getPreview();

  return (
    <div className="px-4 md:px-6 py-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-lg md:text-xl font-bold text-foreground-900">SEO</h1>
          <p className="mt-0.5 text-xs text-foreground-500 font-body">{t('adm2.seo_subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {savedMsg && <span className="text-xs text-green-600 font-label">{savedMsg}</span>}
          <button
            onClick={handleSave}
            className="px-5 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('adm2.save')}
          </button>
        </div>
      </div>

      {/* ── Page Tabs ───────────────────────────────── */}
      <div className="flex items-center gap-1 flex-wrap bg-background-50 rounded-xl border border-background-200/70 p-1">
        {entries.map((e) => (
          <button
            key={e.slug}
            onClick={() => setActivePage(e.slug)}
            className={`px-4 py-2 rounded-lg text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
              activePage === e.slug
                ? 'bg-primary-500 text-white'
                : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
            }`}
          >
            {pageLabel(e)}
          </button>
        ))}
      </div>

      {/* ── SEO Fields ──────────────────────────────── */}
      {activeEntry && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Form Fields */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
            <div className="px-5 py-4 border-b border-background-100">
              <h2 className="font-label text-sm font-semibold text-foreground-900">{pageLabel(activeEntry)} — {t('adm2.meta_tags')}</h2>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">
                  {t('adm2.meta_title')}
                  <span className="ml-1 text-[10px] text-foreground-400 font-normal">({activeEntry.title.length}/60)</span>
                </label>
                <input
                  type="text"
                  value={activeEntry.title}
                  onChange={(e) => updateEntry('title', e.target.value)}
                  maxLength={60}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">
                  {t('adm2.meta_description')}
                  <span className="ml-1 text-[10px] text-foreground-400 font-normal">({activeEntry.description.length}/160)</span>
                </label>
                <textarea
                  value={activeEntry.description}
                  onChange={(e) => updateEntry('description', e.target.value)}
                  maxLength={160}
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm2.keywords')}</label>
                <input
                  type="text"
                  value={activeEntry.keywords}
                  onChange={(e) => updateEntry('keywords', e.target.value)}
                  placeholder={t('admy.seo_keywords_ph')}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm2.og_image_url')}</label>
                <input
                  type="text"
                  value={activeEntry.ogImage}
                  onChange={(e) => updateEntry('ogImage', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Right: Google Preview */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
            <div className="px-5 py-4 border-b border-background-100">
              <h2 className="font-label text-sm font-semibold text-foreground-900">{t('adm2.google_preview')}</h2>
            </div>
            <div className="p-5">
              <div className="border border-background-200 rounded-lg p-4 bg-background-50 max-w-full">
                {preview && (
                  <>
                    <p className="text-sm font-body text-foreground-500 truncate">{preview.url} › {activeEntry.slug !== 'home' ? activeEntry.slug : ''}</p>
                    <p className="text-lg font-body font-semibold text-blue-700 hover:underline cursor-pointer truncate mt-0.5">{preview.title}</p>
                    <p className="text-xs font-body text-foreground-500 leading-relaxed mt-0.5 line-clamp-2">{preview.description}</p>
                  </>
                )}
              </div>
              <p className="mt-3 text-[10px] text-foreground-400 font-body">{t('adm2.google_preview_hint')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}